import { ChevronDownIcon } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import { Badge } from '@/components/ui/badge';
import type { ChipColour } from '@/components/ui/chip-colours';
import { SelectContent, SelectItem } from '@/components/ui/select';
import { cn } from '@/lib/utils';

/** A choice's label and its colour from the chip palette (the label colours). */
export interface ChipOption {
  label: string;
  colour: ChipColour;
}

/**
 * Pick one of a few categories as a coloured chip: the chip is the button, with a small chevron,
 * and the menu lists each choice as its chip. Like StatusSelect, but for categories rather than
 * statuses (a task's size or kind), so the colours come from the chip palette, not the status tones.
 */
export function ChipSelect<V extends string>({
  value,
  onValueChange,
  options,
  disabled,
  className,
  ...aria
}: {
  value: V;
  onValueChange: (value: V) => void;
  /** Every choice in menu order, the current one included. */
  options: Record<V, ChipOption>;
  disabled?: boolean;
  /** Placement only; the chip's look comes from its option. */
  className?: string;
  'aria-label'?: string;
}) {
  const current = options[value];
  return (
    <SelectPrimitive.Root value={value} onValueChange={(v) => onValueChange(v as V)} disabled={disabled}>
      <SelectPrimitive.Trigger
        data-slot="chip-select-trigger"
        {...aria}
        className={cn(
          'inline-flex w-fit shrink-0 rounded-full outline-none transition-[filter] focus-visible:ring-[3px] focus-visible:ring-ring/50 enabled:hover:brightness-95 data-[state=open]:brightness-95 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
      >
        <Badge colour={current?.colour ?? 'gray'}>
          <SelectPrimitive.Value>{current?.label ?? value}</SelectPrimitive.Value>
          <ChevronDownIcon aria-hidden className="-mr-0.5 opacity-70" />
        </Badge>
      </SelectPrimitive.Trigger>
      <SelectContent position="popper" align="start">
        {(Object.keys(options) as V[]).map((key) => (
          <SelectItem key={key} value={key}>
            <Badge colour={options[key].colour}>{options[key].label}</Badge>
          </SelectItem>
        ))}
      </SelectContent>
    </SelectPrimitive.Root>
  );
}
